import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Edit, Trash2, Upload, ChevronLeft, Play, Pause, Volume2, X } from 'lucide-react';

import { audioAPI, categoryAPI } from '../../services/api';
import CategoryCreateDialog from './CategoryCreateDialog';

const AudioSidebar = ({ isOpen, onClose, onAudioSelect, template_content, settemplate_content, currentPageIndex = 0 }) => {
  const [audios, setAudios] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCategoryCreateDialog, setShowCategoryCreateDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    artist: '',
    album: '',
    genre: '',
    year: '',
    tags: '',
    file: null
  });
  const audioRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      fetchAudios();
      fetchCategories();
    }
  }, [isOpen, selectedCategory, searchTerm]);

  // Fetch subcategories when upload form category changes
  useEffect(() => {
    if (uploadForm.category && uploadForm.category !== '') {
      fetchSubCategories(uploadForm.category);
    } else {
      setSubCategories([]);
    }
  }, [uploadForm.category]);

  const fetchAudios = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: 1,
        limit: 50,
        ...(selectedCategory !== 'all' && { category: selectedCategory }),
        ...(searchTerm && { search: searchTerm })
      });

      const resp = await audioAPI.getAll(Object.fromEntries(params))
      const data = resp.data;

      if (data.success) {
        setAudios(data.data.audios || []);
      }
    } catch (error) {
      console.error('Error fetching audios:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      // Fetch only root categories (parentId = null) for audio type
      const response = await categoryAPI.getAll({
        type: 'audio',
        parentId: '', // Empty string means parentId is null (root categories)
        isActive: true
      });

      if (response.data.success) {
        const categoriesData = response.data.data || [];
        // Sort categories by name
        categoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setCategories(categoriesData);
      } else {
        console.error('Failed to load audio categories');
      }
    } catch (error) {
      console.error('Error fetching audio categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubCategories = async (category_id) => {
    try {
      setLoadingSubCategories(true);
      const response = await categoryAPI.getAll({
        parentId: category_id,
        type: 'audio',
        isActive: true
      });

      if (response.data.success) {
        const subCategoriesData = response.data.data || [];
        // Sort subcategories by name
        subCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setSubCategories(subCategoriesData);
      } else {
        setSubCategories([]);
      }
    } catch (error) {
      console.error('Error fetching audio subcategories:', error);
      setSubCategories([]);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const handleCategoryCreated = (newCategory) => {
    // Refresh categories list
    fetchCategories();
    // Auto-select the newly created category
    setUploadForm(prev => ({ ...prev, category: newCategory.id, subcategory: '' }));
  };

  const handlePlay = async (audioId) => {
    try {
      // Stop currently playing audio
      if (currentlyPlaying && audioRefs.current[currentlyPlaying]) {
        audioRefs.current[currentlyPlaying].pause();
        audioRefs.current[currentlyPlaying].currentTime = 0;
      }

      if (currentlyPlaying === audioId) {
        setCurrentlyPlaying(null);
      } else {
        setCurrentlyPlaying(audioId);
        const audioElement = audioRefs.current[audioId];
        if (audioElement) {
          // Ensure audio is loaded before playing
          if (audioElement.readyState < 2) {
            console.log('🎵 Loading audio for playback...');
            await new Promise((resolve, reject) => {
              const onCanPlay = () => {
                audioElement.removeEventListener('canplay', onCanPlay);
                audioElement.removeEventListener('error', onError);
                resolve();
              };
              const onError = (e) => {
                audioElement.removeEventListener('canplay', onCanPlay);
                audioElement.removeEventListener('error', onError);
                reject(e);
              };
              audioElement.addEventListener('canplay', onCanPlay);
              audioElement.addEventListener('error', onError);
              audioElement.load(); // Force load
            });
          }

          console.log('🎵 Playing audio:', audioId);
          await audioElement.play();
        } else {
          console.error('❌ Audio element not found for ID:', audioId);
        }
      }
    } catch (error) {
      console.error('❌ Error playing audio:', error);
      setCurrentlyPlaying(null);
    }
  };

  const handleAudioEnd = (audioId) => {
    if (currentlyPlaying === audioId) {
      setCurrentlyPlaying(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      alert('Please select an audio file');
      return;
    }

    if (!uploadForm.category) {
      alert('Please select a category');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('audioFile', uploadForm.file);
      if (uploadForm.name) formData.append('name', uploadForm.name);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.category) formData.append('category', uploadForm.category);
      if (uploadForm.subcategory) formData.append('subcategory', uploadForm.subcategory);
      if (uploadForm.artist) formData.append('artist', uploadForm.artist);
      if (uploadForm.album) formData.append('album', uploadForm.album);
      if (uploadForm.genre) formData.append('genre', uploadForm.genre);
      if (uploadForm.year) formData.append('year', uploadForm.year);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);

      const response = await fetch('/api/v1/audio', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShowUploadModal(false);
          setUploadForm({
            name: '',
            description: '',
            category: '',
            subcategory: '',
            artist: '',
            album: '',
            genre: '',
            year: '',
            tags: '',
            file: null
          });
          fetchAudios();
          alert('Audio uploaded successfully!');
        }
      } else {
        const errorData = await response.json();
        alert(`Upload failed: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      alert('Failed to upload audio');
    } finally {
      setUploading(false);
    }
  };

  const handleAddAudio = (audio) => {
    // Add audio as a page item (unified structure)
    const currentPage = template_content?.pages?.[currentPageIndex] || {};
    const currentItems = currentPage.items || [];

    const audioDuration = audio.duration || 5;

    const newAudioItem = {
      id: `audio-${Date.now()}`,
      type: 'audio',
      name: audio.name,
      // Audio items don't have visual position/size but have timeline properties
      time: {
        start: 0,
        duration: audioDuration
      },
      // Audio-specific properties
      file: {
        name: audio.name,
        url: audio.directUrl || audio.filePath || `/api/v1/audio/${audio.id}/stream`,
        duration: audioDuration
      },
      trackId: 0, // Default to track 0
      trim: {
        start: 0,
        end: audioDuration
      },
      settings: {
        volume: 1,
        fadeIn: 0,
        fadeOut: 0,
        pan: 0
      },
      // Mark as non-visual item
      isVisual: false,
      layer: -1 // Use negative layer for audio items to separate from visual items
    };

    console.log('🎵 Adding audio item to page items:', {
      name: audio.name,
      start: 0,
      duration: audioDuration,
      trackId: 0
    });

    const updatedTemplate = {
      ...template_content,
      pages: template_content.pages.map((page, idx) =>
        idx === currentPageIndex
          ? { ...page, items: [...currentItems, newAudioItem] }
          : page
      )
    };

    settemplate_content(updatedTemplate);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Audio Library</h2>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
          title="Upload Audio"
        >
          <Upload className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Search and Filters */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search audio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Categories</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Audio List */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : audios.length > 0 ? (
          <div className="space-y-3">
            {audios.map((audio) => (
              <div key={audio.id} className="bg-gray-50 rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3">
                  {/* Play Button */}
                  <button
                    onClick={() => handlePlay(audio.id)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      currentlyPlaying === audio.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {currentlyPlaying === audio.id ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4 ml-0.5" />
                    )}
                  </button>

                  {/* Audio Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {audio.name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {audio.duration ? `${Math.round(audio.duration)}s` : 'Unknown duration'}
                      {audio.category && ` • ${audio.category}`}
                    </p>
                  </div>

                  {/* Add Button */}
                  <button
                    onClick={() => handleAddAudio(audio)}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>

                {/* Hidden Audio Element */}
                <audio
                  ref={(el) => {
                    if (el) {
                      audioRefs.current[audio.id] = el;
                      // Set up error handling
                      el.onerror = (e) => {
                        console.error('❌ Audio loading error for ID:', audio.id, e);
                      };
                      el.onloadstart = () => {
                        console.log('🎵 Loading audio:', audio.id);
                      };
                    }
                  }}
                  onEnded={() => handleAudioEnd(audio.id)}
                  preload="metadata"
                  crossOrigin="anonymous"
                >
                  <source src={`/api/v1/audio/${audio.id}/stream`} type={audio.mimeType || 'audio/mpeg'} />
                  Your browser does not support the audio element.
                </audio>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Volume2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No audio files found</p>
            <p className="text-sm text-gray-400 mt-1">
              {searchTerm ? 'Try adjusting your search' : 'Upload audio files to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Upload Audio</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audio File *
                </label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const name = file.name.replace(/\.[^/.]+$/, '');
                    setUploadForm(prev => ({ ...prev, file, name: prev.name || name }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Audio name"
                />
              </div>

              {/* Category Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Category *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCategoryCreateDialog(true)}
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                      disabled={uploading}
                    >
                      <Plus className="w-3 h-3" />
                      <span>Add missing category</span>
                    </button>
                  </div>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => {
                      setUploadForm(prev => ({ ...prev, category: e.target.value, subcategory: '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={loadingCategories || uploading}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {loadingCategories && (
                    <p className="text-sm text-gray-500 mt-1">Loading categories...</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subcategory
                  </label>
                  <select
                    value={uploadForm.subcategory}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, subcategory: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!uploadForm.category || loadingSubCategories || uploading}
                  >
                    <option value="">Select a subcategory (optional)</option>
                    {subCategories.map((subCategory) => (
                      <option key={subCategory.id} value={subCategory.id}>
                        {subCategory.name}
                      </option>
                    ))}
                  </select>
                  {loadingSubCategories && (
                    <p className="text-sm text-gray-500 mt-1">Loading subcategories...</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Artist
                  </label>
                  <input
                    type="text"
                    value={uploadForm.artist}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, artist: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Artist name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Genre
                  </label>
                  <input
                    type="text"
                    value={uploadForm.genre}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, genre: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Genre"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Audio description"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={uploading || !uploadForm.file}
                >
                  {uploading ? 'Uploading...' : 'Upload Audio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Create Dialog */}
      {showCategoryCreateDialog && (
        <CategoryCreateDialog
          isOpen={showCategoryCreateDialog}
          onClose={() => setShowCategoryCreateDialog(false)}
          onCategoryCreated={handleCategoryCreated}
          type="audio"
        />
      )}
    </div>
  );
};

export default AudioSidebar;
